import React, { useCallback, useMemo, useState } from "react";
import { AgGridTable, LoadingState } from "components";
import { useReport } from "hooks";
import { formatDateSafe, formatCurrency } from "utils/helpers/accounting";
import { ROUTES } from "utils";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";

const today = new Date();
const formatDate = (d: Date) => d.toISOString().slice(0, 10);
const firstOfMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`;
const todayString = formatDate(today);

type PurchaseDetailQuery = {
  from_date: string;
  to_date: string;
  partner_id?: string;
  product_id?: string;
};

const RpPurchaseDetail: React.FC = () => {
  const navigate = useNavigate();
  const { fetchRpPurchaseDetail } = useReport();
  const [filters, setFilters] = useState<PurchaseDetailQuery>({
    from_date: firstOfMonth,
    to_date: todayString,
  });
  const [submitted, setSubmitted] = useState<PurchaseDetailQuery | null>(null);

  const handleFilterChange = useCallback((key: keyof PurchaseDetailQuery, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSubmit = useCallback(
    (event?: React.FormEvent) => {
      event?.preventDefault();
      if (!filters.from_date || !filters.to_date) return;
      setSubmitted(filters);
      fetchRpPurchaseDetail.mutate({
        from_date: filters.from_date,
        to_date: filters.to_date,
        partner_id: filters.partner_id ? Number(filters.partner_id) : undefined,
      });
    },
    [fetchRpPurchaseDetail, filters],
  );

  const records = (fetchRpPurchaseDetail.data?.data?.records ?? fetchRpPurchaseDetail.data?.data ?? []) as any[];

  const summary = useMemo(
    () => records.reduce((s, r) => s + (Number(r.amount) || 0), 0),
    [records],
  );

  const columns = useMemo(
    () => [
      {
        headerName: "STT",
        valueGetter: (p: any) => (p.node?.rowIndex ?? 0) + 1,
        pinned: "left",
        minWidth: 60,
        maxWidth: 70,
        cellClass: "text-center text-gray-500 text-sm",
      },
      {
        headerName: "Số chứng từ",
        field: "voucher_code",
        pinned: "left",
        minWidth: 140,
        cellRenderer: (p: any) => {
          const code = p.value as string;
          const id = p.data?.id;
          if (id && code) {
            return (
              <Link to={`${ROUTES.ACCOUNTING.PURCHASE_DOCUMENT_DETAIL}/${id}`}
                className="text-blue-600 hover:text-blue-800 hover:underline">
                {code}
              </Link>
            );
          }
          return code ?? "";
        },
      },
      {
        headerName: "Ngày hạch toán",
        field: "accounting_date",
        minWidth: 130,
        valueFormatter: (p: any) => formatDateSafe(p.value),
      },
      {
        headerName: "Nhà cung cấp",
        field: "partner_name",
        minWidth: 200,
        flex: 1,
      },
      {
        headerName: "Nội dung",
        field: "description",
        minWidth: 220,
        flex: 1,
        cellClass: "text-gray-600 text-sm",
      },
      {
        headerName: "Hàng hóa",
        field: "product_name",
        minWidth: 180,
      },
      {
        headerName: "ĐVT",
        field: "unit",
        minWidth: 80,
      },
      {
        headerName: "Số lượng",
        field: "quantity",
        minWidth: 100,
        valueFormatter: (p: any) => p.value != null ? Number(p.value).toLocaleString("vi-VN") : "",
        cellClass: "text-right",
      },
      {
        headerName: "Đơn giá",
        field: "unit_price",
        minWidth: 130,
        valueFormatter: (p: any) => p.value != null ? formatCurrency(Number(p.value)) : "",
        cellClass: "text-right text-gray-700",
      },
      {
        headerName: "Thành tiền",
        field: "amount",
        minWidth: 140,
        valueFormatter: (p: any) => p.value != null ? formatCurrency(Number(p.value)) : "",
        cellClass: "text-right font-semibold text-gray-900",
      },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({ sortable: true, resizable: true, filter: "agTextColumnFilter", floatingFilter: true, minWidth: 100 }),
    [],
  );

  const isLoading = fetchRpPurchaseDetail.isPending;

  return (
    <div className="p-6 space-y-5">
      {isLoading && <LoadingState message="Đang tải báo cáo" description="Vui lòng chờ trong giây lát." />}

      <nav className="text-sm text-blue-600 flex items-center gap-1 flex-wrap">
        <span className="cursor-pointer hover:underline font-medium" onClick={() => navigate(ROUTES.PRIVATE_ROOT.DASHBOARD)}>BÁO CÁO</span>
        <span className="text-gray-400">/</span>
        <span className="text-gray-500 cursor-pointer hover:underline">Sổ sách, báo cáo quản trị</span>
        <span className="text-gray-400">/</span>
        <span className="text-gray-700 font-medium">Sổ chi tiết mua hàng</span>
      </nav>

      <h1 className="text-xl font-bold text-gray-900">Sổ chi tiết mua hàng</h1>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Từ ngày <span className="text-red-500">*</span></label>
            <input type="date" value={filters.from_date} onChange={(e) => handleFilterChange("from_date", e.target.value)}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" required />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Đến ngày <span className="text-red-500">*</span></label>
            <input type="date" value={filters.to_date} onChange={(e) => handleFilterChange("to_date", e.target.value)}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" required />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Nhà cung cấp</label>
            <input type="text" value={filters.partner_id || ""} onChange={(e) => handleFilterChange("partner_id", e.target.value || undefined)}
              placeholder="Lựa chọn..."
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Hàng hóa</label>
            <input type="text" value={filters.product_id || ""} onChange={(e) => handleFilterChange("product_id", e.target.value || undefined)}
              placeholder="Lựa chọn..."
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
          </div>
        </div>

        <div className="flex justify-start gap-2">
          <button type="submit" disabled={isLoading}
            className="rounded border border-blue-600 px-4 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 transition">
            Xem số liệu
          </button>
          <button type="button" onClick={handleSubmit} disabled={isLoading}
            className="rounded border border-blue-600 px-4 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 transition">
            Xem báo cáo
          </button>
          <button type="button"
            className="rounded border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition">
            Xuất file Excel
          </button>
        </div>
      </form>

      {submitted && records.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3">
            <p className="text-xs text-gray-500">Số chứng từ</p>
            <p className="text-base font-bold text-gray-800">{records.length}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3">
            <p className="text-xs text-gray-500">Tổng tiền mua hàng</p>
            <p className="text-base font-bold text-blue-700">{formatCurrency(summary)}</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        {submitted ? (
          <>
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-700">
                {records.length} chứng từ · {submitted.from_date} → {submitted.to_date}
              </h2>
            </div>
            <AgGridTable rows={records} columns={columns as any} defaultColDef={defaultColDef} pagination={true} autoHeight />
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <svg className="w-12 h-12 mb-3 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <p className="text-sm">Nhấn <strong>Xem số liệu</strong> hoặc <strong>Xem báo cáo</strong> để tải dữ liệu</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RpPurchaseDetail;
